import { useState, useEffect, Suspense } from 'react';
import { Toaster } from 'sonner';

import { VideoBackground } from './components/background/VideoBackground';
import { PomodoroTimer } from './components/timer/PomodoroTimer';
import { MusicPlayer, DailyGiftGrid, ChatContainer } from './components/lazy';
import { AmbientSoundsPlayer } from './components/music/AmbientSoundsPlayer';
import { LevelDisplay } from './components/level/LevelDisplay';
import { SettingsPopover } from './components/settings/SettingsPopover';
import { OnlinePresenceCounter } from './components/presence/OnlinePresenceCounter';
import { LoginScreen } from './components/auth/LoginScreen';
import DiscordButton from './components/DiscordButton';
import { LoadingSpinner } from './components/LoadingSpinner';
import { AdminActionHandler } from './components/admin/AdminActionHandler';
import { ChunkLoadErrorBoundary } from './components/ChunkLoadErrorBoundary';
import { SnowOverlay } from './components/effects/SnowOverlay';
import SnowToggle from './components/effects/SnowToggle';
import { useSettingsSync } from './hooks/useSettingsSync';
import { useBuffActivation } from './hooks/useBuffActivation';
import { useSystemNotifications } from './hooks/useSystemNotifications';
import { useSettingsStore } from './store/useSettingsStore';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import { getEnvironment } from './lib/environment';
import { canClaimDailyGift } from './lib/userSyncAuth';
import { useSmartPIPMode } from './hooks/useSmartPIPMode';
import { useDeviceType } from './hooks/useDeviceType';

import { ScaleWrapper } from './components/layout/ScaleWrapper';
import { OnboardingTour } from './components/tour';
import { GlobalKeyboardShortcuts } from './components/shortcuts/GlobalKeyboardShortcuts';
import { ShortcutsTrigger } from './components/shortcuts/ShortcutsTrigger';

function AppContent() {
  const { authenticated, loading, error, appUser } = useAuth();
  const settingsSyncComplete = useSettingsStore((state) => state.settingsSyncComplete);

  // Enable cross-device settings sync
  useSettingsSync();

  // Auto-activate event buffs (slingshot for elves, etc.)
  useBuffActivation();

  // Subscribe to admin-triggered system notifications
  useSystemNotifications();

  const [musicPlaying, setMusicPlaying] = useState(false);
  const [showDailyGift, setShowDailyGift] = useState(false);
  const [dailyGiftClaimed, setDailyGiftClaimed] = useState(false);
  const isPIPMode = useSmartPIPMode(750);
  const { isMobile } = useDeviceType();
  const isDiscordMobile = getEnvironment() === 'discord' && isMobile;

  // Check if daily gift available (actual claim happens in DailyGiftGrid to prevent double-claiming)
  useEffect(() => {
    // Wait for settings sync to complete (has DB state)
    if (!settingsSyncComplete || !appUser?.id || dailyGiftClaimed) return;

    // Check if gift available (doesn't claim, just checks eligibility)
    canClaimDailyGift(appUser.id)
      .then((canClaim) => {
        setDailyGiftClaimed(true); // Prevent re-checking

        if (canClaim) {
          // Show daily gift modal - DailyGiftGrid will handle actual claim
          setShowDailyGift(true);
          if (import.meta.env.DEV) console.log('[Daily Gift] Gift available, showing modal');
        } else {
          if (import.meta.env.DEV) console.log('[Daily Gift] Already claimed today');
        }
      })
      .catch((error) => {
        console.error('[Daily Gift] Failed to check gift status:', error);
        setDailyGiftClaimed(true); // Prevent retry loop
      });
  }, [settingsSyncComplete, appUser?.id, appUser?.discord_id, dailyGiftClaimed]);

  // Loading state
  if (loading) {
    return (
      <div className="relative min-h-screen overflow-hidden">
        <VideoBackground />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="relative inline-block mb-8">
              <div className="bounce-loader">
                <div className="circle" />
                <div className="circle" />
                <div className="circle" />
                <div className="shadow" />
                <div className="shadow" />
                <div className="shadow" />
              </div>
            </div>
            <p className="text-white text-xl font-medium">Connecting to Discord...</p>
            <p className="text-white/60 text-sm mt-2">
              Please wait while we authenticate your account
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!authenticated) {
    const environment = getEnvironment();

    // Browser: Show login screen
    if (environment === 'browser') {
      return <LoginScreen />;
    }

    // Discord with error: Show error message
    if (error) {
      return (
        <div className="relative min-h-screen overflow-hidden">
          <VideoBackground />
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center max-w-md mx-auto px-4">
              <div className="text-6xl mb-4">⚠️</div>
              <h1 className="text-white text-2xl font-bold mb-2">Authentication Failed</h1>
              <p className="text-white/80 mb-4">
                {error || 'Unable to connect to Discord. Please try again.'}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-white text-gray-900 rounded-lg font-bold hover:bg-gray-100 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // Authenticated - show main app
  return (
    <>
      <ScaleWrapper>
        <div className="relative h-screen overflow-hidden flex flex-col">
          {/* Video Background */}
          <VideoBackground />

          {/* Snow Effect */}
          <SnowOverlay />

          {/* Level Display (Top Left) */}
          <LevelDisplay />

          {/* Online Presence Counter & Shortcuts Trigger (Top Right, below settings button) */}
          {!isPIPMode && (
            <div
              className={`fixed right-4 z-10 flex flex-col items-end gap-2 ${!isDiscordMobile ? 'top-20' : ''}`}
              style={isDiscordMobile ? { top: 'calc(5rem + var(--safe-area-top))' } : undefined}
            >
              <ShortcutsTrigger />
              <OnlinePresenceCounter />
            </div>
          )}

          {/* Main Content - Centered Timer */}
          <div className="flex-1 flex items-center justify-center px-4">
            <PomodoroTimer />
          </div>

          {/* Music Player (Bottom) - Always mounted, UI hidden in PiP */}
          <ChunkLoadErrorBoundary
            onError={(error) => console.error('[MusicPlayer] Chunk load failed:', error)}
          >
            <Suspense fallback={<LoadingSpinner />}>
              <MusicPlayer
                playing={musicPlaying}
                setPlaying={setMusicPlaying}
                isPIPMode={isPIPMode}
              />
            </Suspense>
          </ChunkLoadErrorBoundary>

          {/* Ambient Sounds Player (Hidden) - Always playing, even in PiP */}
          <AmbientSoundsPlayer musicPlaying={musicPlaying} />

          {/* Daily Gift Grid */}
          <ChunkLoadErrorBoundary
            fallback={
              <div className="flex items-center justify-center p-8">
                <div className="text-gray-400">Failed to load rewards calendar</div>
              </div>
            }
            onError={(error) => {
              console.error('[DailyGiftGrid] Chunk load failed:', error);
            }}
          >
            <Suspense fallback={<LoadingSpinner />}>
              <DailyGiftGrid show={showDailyGift} onClose={() => setShowDailyGift(false)} />
            </Suspense>
          </ChunkLoadErrorBoundary>

          {/* Top Right Buttons - Discord & Settings */}
          {!isPIPMode && (
            <div
              className={`fixed right-4 z-[60] flex items-center gap-2 ${!isDiscordMobile ? 'top-4' : ''}`}
              style={isDiscordMobile ? { top: 'calc(1rem + var(--safe-area-top))' } : undefined}
            >
              {/* Sentry Test Button */}

              {!isMobile && <SnowToggle />}
              <DiscordButton />
              <SettingsPopover />
            </div>
          )}

          {/* Chat Container (Bottom Left) */}
          {!isPIPMode && (
            <ChunkLoadErrorBoundary
              onError={(error) => console.error('[ChatContainer] Chunk load failed:', error)}
            >
              <Suspense fallback={<LoadingSpinner />}>
                <ChatContainer />
              </Suspense>
            </ChunkLoadErrorBoundary>
          )}

          {/* Toaster for notifications */}
          <Toaster position="top-center" />

          {/* Admin Action Handler (URL params) */}
          <AdminActionHandler />

          {/* Global Keyboard Shortcuts */}
          <GlobalKeyboardShortcuts toggleMusic={() => setMusicPlaying(!musicPlaying)} />
        </div>
      </ScaleWrapper>

      {/* Onboarding Tour - Outside ScaleWrapper to avoid z-index/transform stacking issues */}
      <OnboardingTour />
    </>
  );
}

// Wrapper component with providers
function App() {
  return (
    <AuthProvider>
      <ChatProvider>
        <AppContent />
      </ChatProvider>
    </AuthProvider>
  );
}

export default App;
